<script setup lang="ts">
const props = defineProps<{ image: string, imageAlt?: string }>()
const { public: { twicpics: { domain } } } = useRuntimeConfig()
const baseSrcURL = new URL(useTwicPicsBlogUrl(props.image), domain)

const getImageHref = ({ width }: { width: number }) => {
  const ratio = 16 / 8
  const url = new URL(baseSrcURL)
  url.search = `twic=v1/focus=auto/cover=${width}x${width / ratio}` // prevent parameters to be encoded
  return url.href
}

const getSrcSetRow = ({ width }: { width: number }) => `${getImageHref({ width })} ${width}w`

// arbitrary choice of 150px step with support of DPR 3
useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      media: '(max-width: 1247px)',
      href: getImageHref({ width: 320 }),
      imagesrcset: `
        ${getSrcSetRow({ width: 300 })},
        ${getSrcSetRow({ width: 450 })},
        ${getSrcSetRow({ width: 600 })},
        ${getSrcSetRow({ width: 750 })},
        ${getSrcSetRow({ width: 900 })},
        ${getSrcSetRow({ width: 1050 })},
        ${getSrcSetRow({ width: 1200 })},
        ${getSrcSetRow({ width: 1248 })},
        ${getSrcSetRow({ width: 1350 })},
        ${getSrcSetRow({ width: 1500 })},
        ${getSrcSetRow({ width: 1800 })},
        ${getSrcSetRow({ width: 2100 })},
        ${getSrcSetRow({ width: 2496 })},
        ${getSrcSetRow({ width: 2250 })},
        ${getSrcSetRow({ width: 2400 })},
        ${getSrcSetRow({ width: 2700 })},
        ${getSrcSetRow({ width: 3600 })},
      `,
      imagesizes: '100vw',
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      media: '(min-width: 1248px)',
      href: getImageHref({ width: 1248 }),
      imagesrcset: `
        ${getSrcSetRow({ width: 1248 })},
        ${getSrcSetRow({ width: 2496 })},
        ${getSrcSetRow({ width: 3744 })}
      `,
      imagesizes: '1248px',
      fetchpriority: 'high',
    },
  ],
})
</script>

<template>
  <div class="relative sm:container">
    <img
      :src="getImageHref({ width: 320 })"
      :srcset="`
        ${getSrcSetRow({ width: 300 })},
        ${getSrcSetRow({ width: 450 })},
        ${getSrcSetRow({ width: 600 })},
        ${getSrcSetRow({ width: 750 })},
        ${getSrcSetRow({ width: 900 })},
        ${getSrcSetRow({ width: 1050 })},
        ${getSrcSetRow({ width: 1200 })},
        ${getSrcSetRow({ width: 1248 })},
        ${getSrcSetRow({ width: 1350 })},
        ${getSrcSetRow({ width: 1500 })},
        ${getSrcSetRow({ width: 1800 })},
        ${getSrcSetRow({ width: 2100 })},
        ${getSrcSetRow({ width: 2250 })},
        ${getSrcSetRow({ width: 2400 })},
        ${getSrcSetRow({ width: 2496 })},
        ${getSrcSetRow({ width: 2700 })},
        ${getSrcSetRow({ width: 3600 })},
        ${getSrcSetRow({ width: 3744 })}
      `"
      :sizes="`
        (max-width: 1248px) 100vw,
        1248px
      `"
      fetchpriority="high"
      :alt="props.imageAlt"
      class="aspect-[16/8] object-cover w-full sm:rounded-b"
    >
    <div class="w-full space-y-3 bg-white p-4 lg:absolute lg:bottom-0 lg:max-w-xl lg:rounded-tr lg:p-8">
      <div
        v-if="$slots.suptitle"
        class="text-xs font-semibold uppercase text-pink-500"
      >
        <slot name="suptitle" />
      </div>
      <h1
        v-if="$slots.title"
        class="text-xl font-semibold md:text-2xl lg:text-4xl"
      >
        <slot name="title" />
      </h1>
      <div
        v-if="$slots.subtitle"
        class="text-lg line-clamp-4"
      >
        <slot name="subtitle" />
      </div>
    </div>
  </div>
</template>
